import { db } from "./DBHelper";
import { COLLECTIONS, PRINTER_COMMANDS } from "../const";
import DateTimeHelpers from "./DateTimeHelpers";
import { buildShopHeader } from "./ReportHelper";
import { textToBytes, mergeBytes, padToTwoSides } from "./StringHelper";
import { roundOff } from "./NumberFormatHelper";

/**
 * Detects differences between original and edited sale and produces a human-readable summary.
 */
export const generateEditedSaleRemark = (originalSale, finalSale) => {
  if (!originalSale || !finalSale) return "";

  const changes = [];

  // 1. Payment mode changes
  const getActivePayments = (modes = []) => {
    const map = new Map();
    (modes || []).forEach((m) => {
      const amt = Number(m.amount) || 0;
      const lower = (m.name || "").toLowerCase();
      if (lower === "discount" || lower === "discount %" || lower === "discount percentage") return;
      const cur = map.get(m.name) || 0;
      map.set(m.name, cur + amt);
    });
    for (const [name, amt] of map.entries()) {
      if (amt <= 0) {
        map.delete(name);
      }
    }
    return map;
  };

  const oldPayMap = getActivePayments(originalSale.payment_modes);
  const newPayMap = getActivePayments(finalSale.payment_modes);

  let isPaymentChanged = oldPayMap.size !== newPayMap.size;
  if (!isPaymentChanged) {
    for (const [name, amt] of newPayMap.entries()) {
      if (oldPayMap.get(name) !== amt) {
        isPaymentChanged = true;
        break;
      }
    }
  }

  if (isPaymentChanged) {
    // 1-to-1 mode swap (e.g. CASH 140 -> UPI 140)
    if (oldPayMap.size === 1 && newPayMap.size === 1) {
      const [oldName, oldAmt] = Array.from(oldPayMap.entries())[0];
      const [newName, newAmt] = Array.from(newPayMap.entries())[0];
      if (oldName !== newName) {
        changes.push(`Payment mode changed from ${oldName} (${oldAmt}) to ${newName} (${newAmt})`);
      } else {
        changes.push(`${oldName} payment updated from ${oldAmt} to ${newAmt}`);
      }
    } else {
      const paymentParts = [];
      newPayMap.forEach((newAmt, name) => {
        if (oldPayMap.has(name)) {
          const oldAmt = oldPayMap.get(name);
          if (oldAmt !== newAmt) {
            paymentParts.push(`${name} payment updated from ${oldAmt} to ${newAmt}`);
          } else {
            paymentParts.push(`${name} (${newAmt})`);
          }
        } else {
          paymentParts.push(`${name} (${newAmt})`);
        }
      });

      oldPayMap.forEach((oldAmt, name) => {
        if (!newPayMap.has(name)) {
          paymentParts.push(`- ${name} (${oldAmt})`);
        }
      });

      if (paymentParts.length > 0) {
        changes.push(`${paymentParts.join(", ")}`);
      }
    }
  }

  // 2. Payable amount changes
  const oldPayable = Number(originalSale.payable ?? originalSale.previous_payable ?? 0);
  const newPayable = Number(finalSale.payable ?? 0);
  if (oldPayable !== newPayable) {
    changes.push(`Payable changed from ${oldPayable} to ${newPayable}`);
  }

  // 3. Customer changed check
  const getCustomerLabel = (c) => {
    if (!c) return "";
    if (typeof c === "string") return c;
    return c.name || c.phone || c.mobile || (c.id ? `Customer #${c.id}` : "Customer");
  };
  const oldCustLabel = getCustomerLabel(originalSale.customer);
  const newCustLabel = getCustomerLabel(finalSale.customer);
  const oldCustId = originalSale.customer?.id;
  const newCustId = finalSale.customer?.id;

  const isOldCustExist = Boolean(originalSale.customer && (oldCustId || oldCustLabel));
  const isNewCustExist = Boolean(finalSale.customer && (newCustId || newCustLabel));

  if (!isOldCustExist && isNewCustExist) {
    changes.push(`Customer added: ${newCustLabel}`);
  } else if (isOldCustExist && !isNewCustExist) {
    changes.push(`Customer removed: ${oldCustLabel}`);
  } else if (isOldCustExist && isNewCustExist) {
    const idChanged = oldCustId && newCustId && String(oldCustId) !== String(newCustId);
    const labelChanged = oldCustLabel !== newCustLabel;
    if (idChanged || labelChanged) {
      changes.push(`Customer: ${oldCustLabel} → ${newCustLabel}`);
    }
  }

  // 4. Items changes (additions, removals, qty edits)
  const getItemKey = (item) => {
    if (item.variant_id) return `${item.id || item.item_id || item.name}_${item.variant_id}`;
    return String(item.id || item.item_id || item.local_id || item.name);
  };
  const getItemName = (item) => {
    return item.variant_name ? `${item.name} (${item.variant_name})` : item.name || "Item";
  };

  const oldMap = new Map();
  (originalSale.items || []).forEach((i) => {
    const key = getItemKey(i);
    const cur = oldMap.get(key);
    if (cur) {
      cur.qty = (Number(cur.qty) || 0) + (Number(i.qty) || 0);
    } else {
      oldMap.set(key, { ...i, qty: Number(i.qty) || 0 });
    }
  });

  const newMap = new Map();
  (finalSale.items || []).forEach((i) => {
    const key = getItemKey(i);
    const cur = newMap.get(key);
    if (cur) {
      cur.qty = (Number(cur.qty) || 0) + (Number(i.qty) || 0);
    } else {
      newMap.set(key, { ...i, qty: Number(i.qty) || 0 });
    }
  });

  newMap.forEach((newItem, key) => {
    const oldItem = oldMap.get(key);
    const name = getItemName(newItem);
    if (!oldItem) {
      changes.push(`+ ${name} (x${newItem.qty})`);
    } else if (Number(oldItem.qty) !== Number(newItem.qty)) {
      changes.push(`${name} qty: ${oldItem.qty} → ${newItem.qty}`);
    }
  });

  oldMap.forEach((oldItem, key) => {
    if (!newMap.has(key)) {
      const name = getItemName(oldItem);
      changes.push(`- ${name} (x${oldItem.qty})`);
    }
  });

  // 5. Discount changes
  const oldDiscount = Number(originalSale.discount || 0) + Number(originalSale.discount_applied_on_items || 0);
  const newDiscount = Number(finalSale.discount || 0) + Number(finalSale.discount_applied_on_items || 0);
  if (oldDiscount !== newDiscount) {
    changes.push(`Discount: ${oldDiscount} → ${newDiscount}`);
  }

  // 6. Order type changes
  const getOrderTypeName = (ot) => (ot && typeof ot === "object" ? ot.name : ot) || "";
  const oldOT = getOrderTypeName(originalSale.order_type);
  const newOT = getOrderTypeName(finalSale.order_type);
  if (oldOT && newOT && oldOT !== newOT) {
    changes.push(`Order Type: ${oldOT} → ${newOT}`);
  }

  return changes.join("; ");
};

/**
 * Splits a remark string into individual lines for UI display,
 * ensuring each edit remark appears on a new line after a comma.
 */
export const splitRemarkLines = (remark) => {
  if (!remark) return [];
  return String(remark)
    .replace(/,\s*(?=Edited at\b)/g, ",\n")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
};

/**
 * Generates and attaches the timestamped edit remark to finalSale.
 */
export const attachEditedSaleRemark = (originalSale, finalSale, description = "") => {
  if (!originalSale || !finalSale) return;
  const changeRemark = generateEditedSaleRemark(originalSale, finalSale);
  const desc = description || finalSale.description;
  if (changeRemark) {
    const time = DateTimeHelpers.formatDateTime(new Date());
    let remarkEntry = `${changeRemark} at ${time}`;
    if (desc && String(desc).trim()) {
      remarkEntry += `\ndescription: ${String(desc).trim()}`;
    }
    finalSale.remark = finalSale.remark ? `${finalSale.remark},\n${remarkEntry}` : remarkEntry;
  } else if (desc && String(desc).trim()) {
    const time = DateTimeHelpers.formatDateTime(new Date());
    const remarkEntry = `Sale edited at ${time}\ndescription: ${String(desc).trim()}`;
    finalSale.remark = finalSale.remark ? `${finalSale.remark},\n${remarkEntry}` : remarkEntry;
  }
};

/**
 * Saves an edited sale record to the local IndexedDB.
 */
export const saveEditedSale = async (finalSale, shiftId, description = "") => {
  const record = {
    sale: finalSale,
    billNo: finalSale.bill_no || "",
    time: finalSale.time || null,
    editedAt: DateTimeHelpers.getLocalISOString(),
    previousAmount: finalSale.previous_payable ?? 0,
    newAmount: finalSale.payable || 0,
    shiftId: shiftId || null,
    items: finalSale.items || [],
    remark: finalSale.remark || "",
    description: description || "",
  };

  try {
    await db.add(COLLECTIONS.EDITED_SALES.name, record);
  } catch (err) {
    console.error("EditedSaleHelper#saveEditedSale error:", err);
  }
};

/**
 * Retrieves all edited sale records for a given shift.
 */
export const getEditedSalesByShift = async (shiftId) => {
  if (!shiftId) return [];
  try {
    const all = await db.getAll(COLLECTIONS.EDITED_SALES.name);
    return all.filter((r) => String(r.shiftId) === String(shiftId));
  } catch (err) {
    console.error("EditedSaleHelper#getEditedSalesByShift error:", err);
    return [];
  }
};

/**
 * Builds an ESC/POS byte stream for the edited sales print slip.
 */
export const getEditedSalesPrintText = (records = [], shop, shift) => {
  const lines = [];
  const lineWidth = 48;

  buildShopHeader(lines);

  lines.push(PRINTER_COMMANDS.ALIGN_CENTER);
  lines.push(PRINTER_COMMANDS.CHARS_SIZE_DOUBLE);
  lines.push(textToBytes("EDITED SALES\n"));
  lines.push(PRINTER_COMMANDS.CHARS_SIZE_NORMAL);
  lines.push(PRINTER_COMMANDS.ALIGN_LEFT);

  lines.push(textToBytes(padToTwoSides("Salesperson:", "All")));
  lines.push(
    textToBytes(padToTwoSides("Shift:", shift ? `${DateTimeHelpers.getDate(shift.opened_at)}-${shift.name}` : "-")),
  );
  if (shift?.id) {
    lines.push(textToBytes(padToTwoSides("CLOUD ID:", String(shift.id))));
  }
  lines.push(textToBytes(padToTwoSides("Generated On:", DateTimeHelpers.formatDateTime(new Date()))));

  lines.push(textToBytes("=".repeat(lineWidth)));

  lines.push(
    textToBytes(
      "#" + "Bill No".padStart(11) + "Edited At".padStart(13) + "Old Amt".padStart(11) + "New Amt".padStart(12),
    ),
  );
  lines.push(textToBytes("-".repeat(lineWidth)));

  records.forEach((r, idx) => {
    const indexStr = String(idx + 1).padEnd(2);
    const billNo = String(r.billNo || "-");
    const editedAtTime = r.editedAt ? DateTimeHelpers.getLocalTime(r.editedAt) : "-";
    const oldAmt = roundOff(r.previousAmount || 0);
    const newAmt = roundOff(r.newAmount || 0);

    let left = "";
    left += indexStr.padEnd(3);
    left += billNo.padEnd(12);
    left += editedAtTime.padStart(10);
    left += oldAmt.padStart(11);
    left += newAmt.padStart(12);

    lines.push(textToBytes(left));
  });

  lines.push(textToBytes("-".repeat(lineWidth)));

  lines.push(textToBytes("Total Sales Edited:".padEnd(38) + String(records.length).padStart(10)));

  lines.push(PRINTER_COMMANDS.FEED_PAPER);
  lines.push(PRINTER_COMMANDS.CUT_CHARS);
  lines.push(PRINTER_COMMANDS.BEEP_SOUND);

  return mergeBytes(lines);
};

export const computeEditedSaleTotals = (records = []) => {
  return {
    count: records.length,
    totalAmount: records.reduce((sum, r) => sum + Number(r.newAmount || 0), 0),
  };
};
